import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { useToast } from '@/components/Toast'
import { apiClient, unwrap } from '@/lib/apiClient'
import { useCurrentUser } from '@/lib/hooks'
import { HAS_AUTH } from '@/lib/auth'
import type {
  EnrichmentRawInputs,
  EnrichmentStatusResponse,
} from '@/types'
import { cn } from '@/lib/cn'

// =============================================================================
// JarvisProfileCard — "Build my Jarvis profile" section in Settings.
// =============================================================================
// Shows the current enrichment summary (if any), with three actions:
//   - Scan now / Re-scan      → re-runs the full Apollo + scraper pipeline
//   - Edit inputs              → expose the email/linkedin/site/phone form
//   - Delete my profile data   → wipes the enrichment record
// =============================================================================

export function JarvisProfileCard() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { data: user } = useCurrentUser()

  const enrichment = user?.enrichment ?? null
  const status = enrichment?.status ?? 'none'
  const isPolling = status === 'pending'

  const { data: statusData } = useQuery<EnrichmentStatusResponse>({
    queryKey: ['users', 'enrichment-status'],
    queryFn: () =>
      unwrap<EnrichmentStatusResponse>(apiClient.get('/users/enrichment-status')),
    refetchInterval: isPolling ? 2_000 : false,
    enabled: HAS_AUTH,
  })

  // Refetch user when the poll says we just finished — drops the spinner.
  useEffect(() => {
    if (!isPolling) return
    if (statusData?.status === 'complete' || statusData?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    }
  }, [isPolling, statusData?.status, queryClient])

  const [editing, setEditing] = useState(false)
  const [email, setEmail] = useState('')
  const [linkedInUrl, setLinkedInUrl] = useState('')
  const [companySite, setCompanySite] = useState('')
  const [phone, setPhone] = useState('')

  useMemo(() => {
    // Seed form values from the existing record so editing feels continuous.
    setEmail(enrichment?.rawInputs?.email ?? user?.email ?? '')
    setLinkedInUrl(
      enrichment?.rawInputs?.linkedInUrl ?? enrichment?.person?.linkedInUrl ?? '',
    )
    setCompanySite(enrichment?.rawInputs?.companySite ?? enrichment?.company?.domain ?? '')
    setPhone(enrichment?.rawInputs?.phone ?? enrichment?.person?.phone ?? '')
  }, [enrichment, user?.email])

  const startScan = useMutation({
    mutationFn: (body: EnrichmentRawInputs) =>
      unwrap(apiClient.post('/users/enrich', body)),
    onSuccess: () => {
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'enrichment-status'] })
      toast.success('Scan started', 'I\'ll update this in a few seconds.')
    },
    onError: (e: Error) => toast.error("Couldn't start the scan", e.message),
  })

  const remove = useMutation({
    mutationFn: () => unwrap(apiClient.delete('/users/enrichment')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'enrichment-status'] })
      toast.success('Profile data deleted')
    },
    onError: (e: Error) => toast.error("Couldn't delete profile data", e.message),
  })

  const submitScan = () => {
    const body: EnrichmentRawInputs = {}
    if (email.trim()) body.email = email.trim()
    if (linkedInUrl.trim()) body.linkedInUrl = linkedInUrl.trim()
    if (companySite.trim()) body.companySite = companySite.trim()
    if (phone.trim()) body.phone = phone.trim()
    startScan.mutate(body)
  }

  return (
    <JarvisCard>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-jarvis-blue" />
            <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
              Build my Jarvis profile
            </h2>
          </div>
          <p className="text-sm text-jarvis-muted mt-2 max-w-lg">
            I use this to ground every response, briefing, and call in your actual business.
            You can re-scan after a company change, or delete it any time.
          </p>
        </div>
        <StatusPill status={status} />
      </div>

      {status === 'complete' && enrichment && !editing && (
        <ProfileSummary
          person={[enrichment.person?.firstName, enrichment.person?.lastName]
            .filter(Boolean)
            .join(' ')}
          title={enrichment.person?.title}
          company={enrichment.company?.name}
          businessSummary={enrichment.website?.businessSummary || enrichment.company?.description}
          location={[enrichment.person?.city, enrichment.person?.state]
            .filter(Boolean)
            .join(', ')}
          industry={enrichment.company?.industry}
        />
      )}

      {(editing || status === 'none' || status === 'skipped' || status === 'failed') && (
        <ScanForm
          email={email}
          linkedInUrl={linkedInUrl}
          companySite={companySite}
          phone={phone}
          onEmail={setEmail}
          onLinkedIn={setLinkedInUrl}
          onSite={setCompanySite}
          onPhone={setPhone}
          onCancel={editing ? () => setEditing(false) : undefined}
          onSubmit={submitScan}
          submitting={startScan.isPending}
        />
      )}

      {status === 'complete' && !editing && (
        <div className="flex gap-2 pt-4 border-t border-jarvis-border/50 mt-4">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors inline-flex items-center gap-1.5"
          >
            <RefreshCw size={12} />
            Re-scan
          </button>
          <button
            type="button"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-red/10 hover:border-jarvis-red/30 hover:text-jarvis-red transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Trash2 size={12} />
            Delete my profile data
          </button>
        </div>
      )}

      {status === 'pending' && (
        <div className="flex items-center gap-3 mt-2 text-sm text-jarvis-muted">
          <Loader2 size={14} className="animate-spin text-jarvis-blue" />
          Scanning…
        </div>
      )}
    </JarvisCard>
  )
}

// -----------------------------------------------------------------------------
// Subcomponents
// -----------------------------------------------------------------------------

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    complete: { label: 'Active', cls: 'text-jarvis-green bg-jarvis-green/10 border-jarvis-green/20' },
    pending: { label: 'Scanning', cls: 'text-jarvis-blue bg-jarvis-blue/10 border-jarvis-blue/20' },
    skipped: { label: 'Skipped', cls: 'text-jarvis-muted bg-jarvis-surface-2 border-jarvis-border' },
    failed: { label: 'No data', cls: 'text-jarvis-amber bg-jarvis-amber/10 border-jarvis-amber/20' },
    none: { label: 'Not run', cls: 'text-jarvis-muted bg-jarvis-surface-2 border-jarvis-border' },
  }
  const entry = map[status] ?? map.none
  return (
    <span
      className={cn(
        'text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border',
        entry.cls,
      )}
    >
      {entry.label}
    </span>
  )
}

interface ProfileSummaryProps {
  person?: string
  title?: string
  company?: string
  businessSummary?: string
  location?: string
  industry?: string
}

function ProfileSummary(props: ProfileSummaryProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      <Row label="Name" value={props.person} />
      <Row label="Title" value={props.title} />
      <Row label="Company" value={props.company} />
      <Row label="Industry" value={props.industry} />
      <Row label="Location" value={props.location} />
      <Row label="What you do" value={props.businessSummary} fullWidth />
    </div>
  )
}

function Row({
  label,
  value,
  fullWidth,
}: {
  label: string
  value?: string
  fullWidth?: boolean
}) {
  return (
    <div className={cn('flex flex-col gap-0.5', fullWidth && 'md:col-span-2')}>
      <span className="text-[11px] uppercase tracking-wide text-jarvis-muted">{label}</span>
      <span className="text-jarvis-text leading-relaxed break-words">
        {value && value.trim().length > 0 ? value : <span className="text-jarvis-muted/60">—</span>}
      </span>
    </div>
  )
}

interface ScanFormProps {
  email: string
  linkedInUrl: string
  companySite: string
  phone: string
  onEmail: (v: string) => void
  onLinkedIn: (v: string) => void
  onSite: (v: string) => void
  onPhone: (v: string) => void
  onCancel?: () => void
  onSubmit: () => void
  submitting: boolean
}

function ScanForm(props: ScanFormProps) {
  return (
    <div className="space-y-3">
      <SmallField
        label="Work email"
        value={props.email}
        onChange={props.onEmail}
        placeholder="kc@yourcompany.com"
      />
      <SmallField
        label="LinkedIn URL"
        value={props.linkedInUrl}
        onChange={props.onLinkedIn}
        placeholder="linkedin.com/in/yourhandle"
      />
      <SmallField
        label="Company site"
        value={props.companySite}
        onChange={props.onSite}
        placeholder="yourcompany.com"
      />
      <SmallField
        label="Phone"
        value={props.phone}
        onChange={props.onPhone}
        placeholder="+1 555 555 0100"
      />

      <div className="flex justify-end gap-2 pt-1">
        {props.onCancel && (
          <button
            type="button"
            onClick={props.onCancel}
            className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-surface transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={props.onSubmit}
          disabled={props.submitting}
          className="text-xs px-4 py-1.5 bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 rounded-lg font-medium transition-colors inline-flex items-center gap-1.5"
        >
          {props.submitting && <Loader2 size={12} className="animate-spin" />}
          Scan now
        </button>
      </div>
    </div>
  )
}

function SmallField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-jarvis-muted mb-1 block">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-jarvis-surface-2 border border-jarvis-border rounded-md px-3 py-1.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
        autoComplete="off"
        spellCheck={false}
      />
    </label>
  )
}
